import React, { useMemo, useCallback, useState } from "react";
import styled from "styled-components";
import mapValues from "lodash/mapValues";
import { Formik, Form, Field } from "formik";
import { Modal, Alert, Input } from "components/bootstrap";
import { Select, Spinner, FormikInput, Link } from "components/common";
import StreamLink from "components/streams/StreamLink";
import IndexSetsTable from "views/logic/fieldactions/ChangeFieldType/IndexSetsTable";
import usePutFieldTypeMutation from "views/logic/fieldactions/ChangeFieldType/hooks/useFieldTypeMutation";
import useStream from "components/streams/hooks/useStream";
import { DocumentationLink } from "components/support";
import DocsHelper from "util/DocsHelper";
import { defaultCompare } from "logic/DefaultCompare";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useSendTelemetryOnMount from "logic/telemetry/useSendTelemetryOnMount";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import FieldSelect from "views/logic/fieldactions/ChangeFieldType/FieldSelect";
import useFieldTypesForMappings from "views/logic/fieldactions/ChangeFieldType/hooks/useFieldTypesForMappings";
import Routes from "routing/Routes";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import ModalSubmit from "components/common/ModalSubmit";
const StyledSelect = styled(Select)`
  width: 400px;
  margin-bottom: 20px;
`;
const StyledLabel = styled.h5`
  font-weight: bold;
  margin-bottom: 5px;
`;
const failureStreamId = "000000000000000000000004";
const FailureStreamLink = () => {
  const {
    data: failureStream,
    isFetching: isFetchingFailureStream,
    isError: isErrorFailureStream
  } = useStream(failureStreamId);
  if (isFetchingFailureStream) return /* @__PURE__ */ React.createElement(Spinner, null);
  return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
    StreamLink,
    {
      stream: isErrorFailureStream ? { id: failureStreamId, title: "Processing and Indexing Failures" } : failureStream
    }
  ), /* @__PURE__ */ React.createElement("i", null, " ", "(", /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.ENTERPRISE }, "Enterprise Plugin"), " required)"));
};
const ChangeFieldTypeModal = ({
  onSubmitCallback = void 0,
  initialSelectedIndexSets,
  onClose,
  showSelectionTable = true,
  showFieldSelect = false,
  initialData = { fieldName: void 0, type: void 0 }
}) => {
  const {
    data: { fieldTypes },
    isLoading: isLoadingFieldTypes
  } = useFieldTypesForMappings();
  const sendTelemetry = useSendTelemetry();
  const currentUser = useCurrentUser();
  const hasFailureStreamAccess = isPermitted(currentUser.permissions, `streams:read:${failureStreamId}`);
  const fieldTypeOptions = useMemo(
    () => Object.entries(fieldTypes).sort(([, label1], [, label2]) => defaultCompare(label1, label2)).map(([value, label]) => ({
      value,
      label
    })),
    [fieldTypes]
  );
  const [indexSetSelection, setIndexSetSelection] = useState(initialSelectedIndexSets);
  const { putFieldTypeMutation, isLoading: fieldTypeMutationIsLoading } = usePutFieldTypeMutation();
  const onSubmit = useCallback(
    ({ rotate: rotated, field_type: type, field: fieldName }) => {
      putFieldTypeMutation({
        indexSetSelection,
        newFieldType: type,
        rotated,
        field: fieldName
      }).then((responseJson) => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_CHANGED, {
          app_action_value: {
            value: "change-field-type",
            rotated,
            isAllIndexesSelected: indexSetSelection.length === initialSelectedIndexSets.length
          }
        });
        if (onSubmitCallback) {
          const response = mapValues(responseJson, (fieldType) => ({
            id: fieldType.field_name,
            origin: fieldType.origin,
            fieldName: fieldType.field_name,
            type: fieldType.type,
            isReserved: fieldType.is_reserved
          }));
          onSubmitCallback(response);
        }
      }).then(() => onClose());
    },
    [
      indexSetSelection,
      initialSelectedIndexSets.length,
      onClose,
      onSubmitCallback,
      putFieldTypeMutation,
      sendTelemetry
    ]
  );
  useSendTelemetryOnMount(sendTelemetry, TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_OPENED, {
    app_action_value: "change-field-type-opened"
  });
  const onCancel = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION.CHANGE_FIELD_TYPE_CLOSED, {
      app_action_value: "change-field-type-closed"
    });
    onClose();
  }, [onClose, sendTelemetry]);
  const initialValues = useMemo(
    () => ({ field: initialData == null ? void 0 : initialData.fieldName, field_type: initialData == null ? void 0 : initialData.type, rotate: true }),
    [initialData == null ? void 0 : initialData.fieldName, initialData == null ? void 0 : initialData.type]
  );
  return /* @__PURE__ */ React.createElement(Formik, { onSubmit, initialValues }, ({ values: { field: fieldName, field_type: type } }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, /* @__PURE__ */ React.createElement("span", null, "Change ", fieldName, " Field Type"))), /* @__PURE__ */ React.createElement(Modal.Body, null, showFieldSelect && /* @__PURE__ */ React.createElement(Field, { name: "field" }, ({ field: { value, name } }) => /* @__PURE__ */ React.createElement(FieldSelect, { indexSetId: initialSelectedIndexSets[0], name, field: value })), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Changing the type of the field ", /* @__PURE__ */ React.createElement("b", null, fieldName), " can have a significant impact on the ingestion of future log messages. If you declare a field to have a type which is incompatible with the logs you are ingesting, it can lead to ingestion errors.", hasFailureStreamAccess && /* @__PURE__ */ React.createElement(React.Fragment, null, "It is recommended to enable", " ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.INDEXER_FAILURES, displayIcon: true, text: "Failure Processing" }), " ", "and watch the ", /* @__PURE__ */ React.createElement(FailureStreamLink, null), " stream closely afterwards.")), /* @__PURE__ */ React.createElement(StyledLabel, null, `Select Field Type For ${fieldName || "Field"}`), /* @__PURE__ */ React.createElement(Field, { name: "field_type" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(Input, { id: name }, /* @__PURE__ */ React.createElement(
    StyledSelect,
    {
      inputId: name,
      options: fieldTypeOptions,
      name,
      onChange: (newValue) => onChange({ target: { name, value: newValue } }),
      value,
      placeholder: "Select field type",
      disabled: isLoadingFieldTypes,
      "aria-label": "Select Field Type For Field",
      required: true
    }
  ))), showSelectionTable && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledLabel, null, "Select Targeted Index Sets"), /* @__PURE__ */ React.createElement("p", null, "By default the ", type ? /* @__PURE__ */ React.createElement("b", null, type) : "selected", " field type will be set for the ", /* @__PURE__ */ React.createElement("b", null, fieldName), " ", "field in all index sets of the current message/search. You can select for which index sets you would like to make the change."), /* @__PURE__ */ React.createElement(
    IndexSetsTable,
    {
      field: fieldName,
      setIndexSetSelection,
      fieldTypes,
      initialSelection: initialSelectedIndexSets
    }
  )), /* @__PURE__ */ React.createElement(StyledLabel, null, "Select Rotation Strategy"), /* @__PURE__ */ React.createElement("p", null, "To see and use the ", type ? /* @__PURE__ */ React.createElement("b", null, type) : "selected field type", " as a field type", fieldName ? /* @__PURE__ */ React.createElement(React.Fragment, null, " ", "for ", /* @__PURE__ */ React.createElement("b", null, fieldName)) : "", ", you have to rotate indices. You can automatically rotate affected indices after submitting this form or do that manually later."), /* @__PURE__ */ React.createElement(Field, { name: "rotate" }, ({ field: { name, onChange } }) => /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      label: "Rotate affected indices after change",
      id: name,
      name,
      onChange
    }
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      disabledSubmit: fieldTypeMutationIsLoading || !(indexSetSelection == null ? void 0 : indexSetSelection.length),
      onCancel,
      submitButtonText: fieldTypeMutationIsLoading ? "Changing field type..." : "Change field type"
    }
  ))));
};
const LoadingBarrier = ({
  isLoading = false,
  show,
  onSubmitCallback = void 0,
  initialSelectedIndexSets,
  onClose,
  showSelectionTable = true,
  showFieldSelect = false,
  initialData = { fieldName: void 0, type: void 0 }
}) => /* @__PURE__ */ React.createElement(Modal, { show, bsSize: "large", onHide: onClose }, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(
  ChangeFieldTypeModal,
  {
    onClose,
    initialSelectedIndexSets,
    onSubmitCallback,
    showSelectionTable,
    showFieldSelect,
    initialData
  }
));
export default LoadingBarrier;
